import { gql } from '@apollo/client';
import { QA_RESULT_FRAGMENT } from '../GameTab/gql/qa-result-graphql';
import { GAME_FILE_FRAGMENT } from '../GameTab/gql/game-file-graphql';
import { SubmissionAPSDetail } from '../../../UserDeveloper/SubmitGame/submit-game-graphql';
import { QAResult } from '@crazygames/game-types/src/submission/qa-results/qa-result.type';
import { UnityEncoding } from '@crazygames/game-types/src/game-build/unity-encoding.type';
import { GameLoaderType } from '@crazygames/game-types/src/game-build/loader.type';
import { GAME_BUILD_STATUS } from '@crazygames/game-types/src/game-build/game-build-status.type';
import { FullscreenSetting } from '@crazygames/game-types/src/game-build/fullscreen.type';
import { GameOrientation } from '@crazygames/game-types/src/game-build/orientation.type';

export const GameBuildStatuses = ['DRAFT', 'SUBMITTED', 'REJECTED', 'ACTIVE', 'ACCEPTED'] as const;
export type GameBuildStatus = (typeof GameBuildStatuses)[number];

export interface UnityConfig {
  encoding?: UnityEncoding;
  saveFileNames?: string[];
}

export interface GameFile {
  uploadFileId: string;
  fileName: string;
  publicUrl: string;
  /**
   * Size in bytes
   */
  size: number;
}

export interface GameBuild {
  // general data
  id: string;
  gameId: string;
  createdAt: string;
  status: GAME_BUILD_STATUS;

  // files
  loaderType: GameLoaderType;
  loaderConfigJson: string;
  gameframeConfigJson: string;
  files?: GameFile[] | null;
  apsConfig: SubmissionAPSDetail;
  unityConfig?: UnityConfig | null;
  totalFileSize?: number | null;
  s3Location?: string | null;

  // options
  fullscreen: FullscreenSetting;
  iosFriendly: boolean;
  androidFriendly: boolean;
  chromebookFriendly: boolean;
  safariFriendly: boolean;
  hasIap: boolean;
  mobileOrientation: GameOrientation | null;

  // admin flags
  allowEmbed: boolean;
  sandbox: boolean;
  isEligibleForMobileHomepage: boolean;

  /** Developer qa result */
  qaResult?: QAResult;
  /** Admin QA result from our content team */
  adminQaResult?: QAResult;
  updateNotes?: string;
}

export const GAME_BUILD_FRAGMENT = gql`
  fragment GameBuildFragment on GameBuild {
    id
    gameId
    createdAt
    status

    loaderType
    loaderConfigJson
    gameframeConfigJson
    files {
      ...GameFileragment
    }
    apsConfig {
      progressType
    }
    unityConfig {
      encoding
      saveFileNames
    }
    totalFileSize
    s3Location

    fullscreen
    iosFriendly
    androidFriendly
    isEligibleForMobileHomepage
    chromebookFriendly
    safariFriendly
    hasIap
    mobileOrientation

    allowEmbed
    sandbox

    qaResult {
      ...QAResultFragment
    }
    adminQaResult {
      ...QAResultFragment
    }
    updateNotes
  }
  ${QA_RESULT_FRAGMENT}
  ${GAME_FILE_FRAGMENT}
`;

export const ADMIN_GAME_BUILDS_QUERY = gql`
  query AdminGetGameBuilds($id: String!) {
    game(id: $id) {
      gameBuilds {
        ...GameBuildFragment
      }
    }
  }
  ${GAME_BUILD_FRAGMENT}
`;

export interface AdminGameBuildsQuery {
  game: {
    gameBuilds: GameBuild[];
  };
}
